// Simple, stable device fingerprint using durable-ish browser + persisted client ID.
// This is NOT for anti-fraud; it just gives us a stable per-device identifier so
// the backend can "activate" this device's social connectors.
import { api } from "@/lib/api";

const CLIENT_ID_KEY = "gg_client_id";
const MAPPED_KEY = "gg_device_mapped";

function randomClientId() {
    // Prefer crypto.randomUUID when available
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    // Fallback: 128 bits of randomness
    const arr = new Uint8Array(16);
    (crypto || window.crypto).getRandomValues(arr);
    return Array.from(arr).map((b) => b.toString(16).padStart(2, "0")).join("");
}

function ensureClientId() {
    let id = null;
    try {
        id = localStorage.getItem(CLIENT_ID_KEY);
    } catch (e) { console.debug("localStorage read failed:", e); }
    if (!id) {
        id = randomClientId();
        try { localStorage.setItem(CLIENT_ID_KEY, id); } catch (e) { console.debug("localStorage write failed:", e); }
    }
    return id;
}

async function sha256Hex(str) {
    const buf = new TextEncoder().encode(str);
    const hash = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export async function computeDeviceFingerprint() {
    const clientId = ensureClientId();
    const parts = [
        clientId,
        navigator.userAgent || "",
        navigator.platform || "",
        navigator.language || "",
        Intl.DateTimeFormat().resolvedOptions().timeZone || "",
        `${window.screen?.width || 0}x${window.screen?.height || 0}`,
        `${window.screen?.colorDepth || 0}`,
    ];
    return sha256Hex(parts.join("|"));
}

export function currentClientId() {
    return ensureClientId();
}

export function getDeviceMeta() {
    return {
        user_agent: navigator.userAgent || "",
        platform: navigator.platform || "",
        language: navigator.language || "",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
        screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    };
}

/**
 * Register this device with the backend for the currently authenticated user.
 * Idempotent — repeated calls just refresh last_seen. Safe to call after every login.
 * Returns { ok, device_id, created } or null if not authenticated.
 */
export async function mapCurrentDevice() {
    try {
        const fingerprint = await computeDeviceFingerprint();
        const meta = getDeviceMeta();
        const { data } = await api.post("/devices/map", { fingerprint, ...meta });
        try { sessionStorage.setItem(MAPPED_KEY, "1"); } catch (e) { console.debug("sessionStorage write failed:", e); }
        return data;
    } catch (e) {
        // Not authenticated is expected here; anything else is worth logging.
        if (e?.response?.status && e.response.status !== 401) console.warn("mapCurrentDevice failed:", e);
        return null;
    }
}

export function isDeviceMapped() {
    try {
        return sessionStorage.getItem(MAPPED_KEY) === "1";
    } catch {
        return false;
    }
}
